/**
 * Belge fiyat motoru (M3 WP 3.1). Teklif, sipariş ve tedarikçi siparişi aynı motoru kullanır; ikinci bir fiyat hesabı yoktur.
 * Sözleşme: `docs/spec/castinflow/sales-documents.md` §3 (kurallar ve yuvarlama noktaları), keşif kanıtı
 * `docs/spec/castinflow/kesif/2026-09-18-tur2/kanit/akis-F04-*.md`.
 *
 * Kurallar (özet):
 * - Tüm tutarlar kanonik ondalık METİNDİR (`decimal.ts`); JS `number` kullanılmaz.
 * - KDV kipi: `exclusive` (fiyatlar KDV hariç), `inclusive` (birim fiyat KDV dahil; net = brüt / (1 + KDV), ikinci vergi bu
 *   bölmeye girmez), `none` (vergi kullanılmaz: KDV ve ikinci vergi 0).
 * - Satır indirimi yüzde ise `net = birim × miktar × (100 − i) / 100`; tutar ise `net = birim × miktar − i` (tutar indirimi
 *   net tabanda uygulanır — keşifte genel indirim tutarının dahil kipte net tabandan düştüğü doğrulandı; aynı kural satıra da
 *   uygulanır).
 * - Satır neti belge ölçeğine (tenant ondalık basamağı) yuvarlanır; toplamlar yuvarlanmış satır netlerinin toplamıdır.
 * - Genel indirim yüzde ise her satırın neti indirilip yeniden yuvarlanır (vergiler indirimli satır üzerinden hesaplanır);
 *   tutar ise yalnız tek KDV oranı ve tek ikinci vergi oranı taşıyan belgede uygulanır, aksi hâlde hata.
 * - İthalat masrafı = miktar × birim ithalat masrafı; genel indirimden etkilenmez.
 * - Vergi grubu (KDV oranı, ikinci vergi oranı) başına: ikinci vergi = (indirimli net + ithalat) × oran; KDV = (indirimli net
 *   + ithalat + ikinci vergi) × KDV oranı; her biri grup başına bir kez yuvarlanır.
 * - Belge tutarı (fırsat/teklif/sipariş/sözleşme tutarı) = KDV hariç indirimli toplam (`discountedNet`).
 * - Opsiyonel satırlar gösterim için hesaplanır, toplama girmez. Başlık satırı hesaplanmaz.
 */
import { MONEY_POLICY, QUANTITY_POLICY, VAT_RATE_POLICY, ZERO, decAdd, decCmp, decDiv, decIsZero, decMul, decSub, parseDecimal, policyIssue, roundTo, toFixedString, } from './decimal.js';
export const PRICING_VAT_MODES = ['exclusive', 'inclusive', 'none'];
export const DISCOUNT_KINDS = ['percent', 'amount'];
export const PRICING_LINE_KINDS = ['item', 'heading'];
/** İndirim yüzdesi: 0–100, 4 ondalık. */
export const DISCOUNT_PERCENT_POLICY = {
    maxIntDigits: 3,
    maxScale: 4,
    min: '0',
    max: '100',
};
/** Belge toplamları: numeric(24,6) — 18 tam basamak. */
export const DOCUMENT_TOTAL_POLICY = { maxIntDigits: 18, maxScale: 6, min: '0' };
/** Satır miktarı: sıfırdan büyük (iade satırı yok). */
export const LINE_QUANTITY_POLICY = { ...QUANTITY_POLICY, min: '0', nonZero: true };
export const SUPPORTED_SCALES = [0, 2, 4, 6];
export const PRICING_ISSUE_CODES = [
    'INVALID_NUMBER',
    'QUANTITY_REQUIRED',
    'PRICE_REQUIRED',
    'VAT_REQUIRED',
    'DISCOUNT_OVER_100',
    'DISCOUNT_EXCEEDS_LINE',
    'DISCOUNT_EXCEEDS_TOTAL',
    'DOCUMENT_DISCOUNT_MIXED_RATES',
    'NO_PRICED_LINES',
    'TOTAL_TOO_LARGE',
];
const HUNDRED = parseDecimal('100');
const ONE = parseDecimal('1');
function read(raw, policy, issues, where) {
    if (raw === null || raw === undefined || raw === '')
        return null;
    let d;
    try {
        d = parseDecimal(raw);
    }
    catch {
        issues.push({ code: 'INVALID_NUMBER', ...where, message: 'geçersiz sayı' });
        return null;
    }
    const p = policyIssue(d, policy);
    if (p !== null) {
        issues.push({ code: 'INVALID_NUMBER', ...where, message: p });
        return null;
    }
    return d;
}
function str(d, scale) {
    return toFixedString(d, scale);
}
/**
 * Belge toplamlarını hesaplar. Girdi hatalıysa `ok: false` ve tüm sorunlar (değer içermeyen iletilerle) döner; kısmi toplam
 * üretilmez.
 */
export function computeDocumentPricing(input) {
    const issues = [];
    const S = input.scale;
    const M = input.rounding;
    const prepared = [];
    input.lines.forEach((l, index) => {
        if (l.kind === 'heading') {
            prepared.push({
                index,
                kind: 'heading',
                excluded: true,
                quantity: ZERO,
                unitPrice: ZERO,
                vat: ZERO,
                secondTax: ZERO,
                importPerUnit: ZERO,
                discount: null,
            });
            return;
        }
        const q = read(l.quantity, LINE_QUANTITY_POLICY, issues, { line: index, field: 'quantity' });
        if (q === null && (l.quantity ?? '') === '')
            issues.push({
                code: 'QUANTITY_REQUIRED',
                line: index,
                field: 'quantity',
                message: 'miktar gerekli',
            });
        const p = read(l.unitPrice, MONEY_POLICY, issues, { line: index, field: 'unitPrice' });
        if (p === null && (l.unitPrice ?? '') === '')
            issues.push({
                code: 'PRICE_REQUIRED',
                line: index,
                field: 'unitPrice',
                message: 'birim fiyat gerekli',
            });
        let vat = ZERO;
        if (input.vatMode !== 'none') {
            const v = read(l.vatRate, VAT_RATE_POLICY, issues, { line: index, field: 'vatRate' });
            if (v === null && (l.vatRate ?? '') === '')
                issues.push({
                    code: 'VAT_REQUIRED',
                    line: index,
                    field: 'vatRate',
                    message: 'KDV oranı gerekli',
                });
            vat = v ?? ZERO;
        }
        let secondTax = ZERO;
        if (input.secondTaxEnabled && input.vatMode !== 'none')
            secondTax =
                read(l.secondTaxRate, VAT_RATE_POLICY, issues, { line: index, field: 'secondTaxRate' }) ??
                    ZERO;
        let importPerUnit = ZERO;
        if (input.importCostEnabled)
            importPerUnit =
                read(l.importCostPerUnit, MONEY_POLICY, issues, {
                    line: index,
                    field: 'importCostPerUnit',
                }) ?? ZERO;
        let discount = null;
        if (l.discount) {
            const policy = l.discount.kind === 'percent' ? DISCOUNT_PERCENT_POLICY : MONEY_POLICY;
            const before = issues.length;
            const dv = read(l.discount.value, policy, issues, { line: index, field: 'discount' });
            if (issues.length > before && l.discount.kind === 'percent') {
                // "100'ü aşan indirim" iletisi ayrı kodla (istemci satırı işaretler)
                const last = issues[issues.length - 1];
                if (last?.message === 'değer çok büyük')
                    last.code = 'DISCOUNT_OVER_100';
            }
            if (dv !== null && !decIsZero(dv))
                discount = { kind: l.discount.kind, value: dv };
        }
        prepared.push({
            index,
            kind: 'item',
            excluded: l.optional === true,
            quantity: q ?? ZERO,
            unitPrice: p ?? ZERO,
            vat,
            secondTax,
            importPerUnit,
            discount,
        });
    });
    let docDiscount = null;
    if (input.documentDiscount) {
        const policy = input.documentDiscount.kind === 'percent' ? DISCOUNT_PERCENT_POLICY : MONEY_POLICY;
        const before = issues.length;
        const dv = read(input.documentDiscount.value, policy, issues, { field: 'documentDiscount' });
        if (issues.length > before && input.documentDiscount.kind === 'percent') {
            const last = issues[issues.length - 1];
            if (last?.message === 'değer çok büyük')
                last.code = 'DISCOUNT_OVER_100';
        }
        if (dv !== null && !decIsZero(dv))
            docDiscount = { kind: input.documentDiscount.kind, value: dv };
    }
    if (issues.length > 0)
        return { ok: false, issues };
    const results = [];
    const lineNet = new Map();
    for (const l of prepared) {
        if (l.kind === 'heading') {
            results.push({
                index: l.index,
                kind: 'heading',
                excluded: true,
                grossNet: str(ZERO, S),
                lineDiscount: str(ZERO, S),
                net: str(ZERO, S),
                documentDiscount: null,
                discountedNet: null,
                importCost: str(ZERO, S),
                vatRate: '0',
                secondTaxRate: '0',
            });
            continue;
        }
        const divisor = input.vatMode === 'inclusive' ? decAdd(ONE, decDiv(l.vat, HUNDRED, 40, 'half_up')) : ONE;
        const grossBrut = decMul(l.unitPrice, l.quantity);
        // Bölme en sonda: yüzde indirim brüt tabanda uygulanıp tek bölmeyle nete çevrilir (tekrarlayan ondalık birikmez)
        const grossNet = decDiv(grossBrut, divisor, 20, 'half_up');
        let net;
        if (l.discount?.kind === 'percent') {
            net = decDiv(decMul(grossBrut, decSub(HUNDRED, l.discount.value)), decMul(HUNDRED, divisor), 20, 'half_up');
        }
        else if (l.discount?.kind === 'amount') {
            if (decCmp(l.discount.value, grossNet) > 0) {
                issues.push({
                    code: 'DISCOUNT_EXCEEDS_LINE',
                    line: l.index,
                    field: 'discount',
                    message: 'indirim satır tutarını aşıyor',
                });
                continue;
            }
            net = decSub(grossNet, l.discount.value);
        }
        else {
            net = grossNet;
        }
        const grossR = roundTo(grossNet, S, M);
        const netR = roundTo(net, S, M);
        lineNet.set(l.index, netR);
        results.push({
            index: l.index,
            kind: 'item',
            excluded: l.excluded,
            grossNet: str(grossR, S),
            lineDiscount: str(decSub(grossR, netR), S),
            net: str(netR, S),
            documentDiscount: null,
            discountedNet: str(netR, S),
            importCost: str(roundTo(decMul(l.quantity, l.importPerUnit), S, M), S),
            vatRate: l.vat.toString(),
            secondTaxRate: l.secondTax.toString(),
        });
    }
    if (issues.length > 0)
        return { ok: false, issues };
    const included = prepared.filter((l) => l.kind === 'item' && !l.excluded);
    if (included.length === 0 && docDiscount !== null)
        return {
            ok: false,
            issues: [
                {
                    code: 'NO_PRICED_LINES',
                    field: 'documentDiscount',
                    message: 'indirim uygulanacak satır yok',
                },
            ],
        };
    const groups = new Map();
    const groupOf = (l) => {
        const key = `${l.vat.toString()}|${l.secondTax.toString()}`;
        let g = groups.get(key);
        if (!g) {
            g = { vat: l.vat, secondTax: l.secondTax, base: ZERO, importCost: ZERO };
            groups.set(key, g);
        }
        return g;
    };
    let netAfterLine = ZERO;
    for (const l of included)
        netAfterLine = decAdd(netAfterLine, lineNet.get(l.index) ?? ZERO);
    if (docDiscount?.kind === 'amount') {
        const keys = new Set(included.map((l) => `${l.vat.toString()}|${l.secondTax.toString()}`));
        if (keys.size > 1)
            return {
                ok: false,
                issues: [
                    {
                        code: 'DOCUMENT_DISCOUNT_MIXED_RATES',
                        field: 'documentDiscount',
                        message: 'tutar indirimi yalnız tek KDV ve tek ikinci vergi oranlı belgede uygulanır',
                    },
                ],
            };
        if (decCmp(docDiscount.value, netAfterLine) > 0)
            return {
                ok: false,
                issues: [
                    {
                        code: 'DISCOUNT_EXCEEDS_TOTAL',
                        field: 'documentDiscount',
                        message: 'indirim belge tutarını aşıyor',
                    },
                ],
            };
    }
    for (const l of included) {
        const g = groupOf(l);
        const netR = lineNet.get(l.index) ?? ZERO;
        const r = results.find((x) => x.index === l.index);
        let discounted = netR;
        if (docDiscount?.kind === 'percent') {
            discounted = roundTo(decDiv(decMul(netR, decSub(HUNDRED, docDiscount.value)), HUNDRED, 20, 'half_up'), S, M);
            if (r) {
                r.documentDiscount = str(decSub(netR, discounted), S);
                r.discountedNet = str(discounted, S);
            }
        }
        else if (docDiscount?.kind === 'amount' && r) {
            r.documentDiscount = null;
            r.discountedNet = null;
        }
        g.base = decAdd(g.base, discounted);
        g.importCost = decAdd(g.importCost, roundTo(decMul(l.quantity, l.importPerUnit), S, M));
    }
    if (docDiscount?.kind === 'amount') {
        const only = [...groups.values()][0];
        if (only)
            only.base = decSub(only.base, docDiscount.value);
    }
    let base = ZERO;
    let importCost = ZERO;
    let secondTax = ZERO;
    let vat = ZERO;
    const breakdown = [];
    for (const g of groups.values()) {
        const st = roundTo(decDiv(decMul(decAdd(g.base, g.importCost), g.secondTax), HUNDRED, 20, 'half_up'), S, M);
        const v = roundTo(decDiv(decMul(decAdd(decAdd(g.base, g.importCost), st), g.vat), HUNDRED, 20, 'half_up'), S, M);
        base = decAdd(base, g.base);
        importCost = decAdd(importCost, g.importCost);
        secondTax = decAdd(secondTax, st);
        vat = decAdd(vat, v);
        breakdown.push({
            vatRate: g.vat.toString(),
            secondTaxRate: g.secondTax.toString(),
            base: str(g.base, S),
            importCost: str(g.importCost, S),
            secondTax: str(st, S),
            vat: str(v, S),
        });
    }
    breakdown.sort((a, b) => decCmp(parseDecimal(a.vatRate), parseDecimal(b.vatRate)));
    let grossNet = ZERO;
    let lineDiscount = ZERO;
    for (const r of results)
        if (!r.excluded) {
            grossNet = decAdd(grossNet, parseDecimal(r.grossNet));
            lineDiscount = decAdd(lineDiscount, parseDecimal(r.lineDiscount));
        }
    const grand = decAdd(decAdd(decAdd(base, importCost), secondTax), vat);
    if (policyIssue(grand, DOCUMENT_TOTAL_POLICY) !== null ||
        policyIssue(grossNet, DOCUMENT_TOTAL_POLICY) !== null)
        return {
            ok: false,
            issues: [{ code: 'TOTAL_TOO_LARGE', field: 'lines', message: 'belge toplamı çok büyük' }],
        };
    return {
        ok: true,
        lines: results,
        totals: {
            grossNet: str(grossNet, S),
            lineDiscount: str(lineDiscount, S),
            netAfterLineDiscount: str(netAfterLine, S),
            documentDiscount: str(decSub(netAfterLine, base), S),
            discountedNet: str(base, S),
            importCost: str(importCost, S),
            secondTax: str(secondTax, S),
            vat: str(vat, S),
            grandTotal: str(grand, S),
            breakdown,
        },
    };
}
/** Etkin toplam indirim yüzdesi (satır + genel) — onay eşikleri için; brüt net sıfırsa `0`. */
export function effectiveDiscountPercent(totals) {
    const gross = parseDecimal(totals.grossNet);
    if (decIsZero(gross))
        return '0';
    const disc = decSub(gross, parseDecimal(totals.discountedNet));
    return toFixedString(decDiv(decMul(disc, HUNDRED), gross, 4, 'half_up'), 4);
}
/** Satırların en yüksek indirim yüzdesi (yüzde indirimler; tutar indirimleri satır brütüne oranlanır). */
export function maxLineDiscountPercent(lines) {
    let max = ZERO;
    for (const l of lines) {
        if (l.kind !== 'item' || l.excluded)
            continue;
        const g = parseDecimal(l.grossNet);
        if (decIsZero(g))
            continue;
        const pct = decDiv(decMul(parseDecimal(l.lineDiscount), HUNDRED), g, 4, 'half_up');
        if (decCmp(pct, max) > 0)
            max = pct;
    }
    return toFixedString(max, 4);
}
//# sourceMappingURL=pricing.js.map